import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineRounded";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { CircularProgress, ListItemIcon, Menu, Typography } from "@mui/material";
import { useCallback } from "react";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import { MenuItemStyled } from "@entrilia/shared/components/MenuItemStyled";

import type { MeasureDescriptor } from "../../../../../../../../../api/types/svTypes.ts";
import { isMeasureMeta } from "../../utilities/metaUtils.ts";

interface Props {
  x: number;
  y: number;
  measure: MeasureDescriptor;
  readonly: boolean;
  isLoading: boolean;
  onExplain: (measure: MeasureDescriptor) => void;
  onChartOfAccounts: (measure: MeasureDescriptor) => void;
  onDuplicate: (measure: MeasureDescriptor) => void;
  onEdit: (measure: MeasureDescriptor) => void;
  onMoveToSubgroup?: (measure: MeasureDescriptor) => void;
  onDelete: (measure: MeasureDescriptor) => void;
  onToggleLock: (measure: MeasureDescriptor) => void;
  onClose: () => void;
  onUsedByReports: (measure: MeasureDescriptor) => void;
}

const MeasureMenu = ({
  x,
  y,
  measure,
  readonly,
  isLoading,
  onExplain,
  onChartOfAccounts,
  onDuplicate,
  onEdit,
  onMoveToSubgroup,
  onDelete,
  onToggleLock,
  onClose,
  onUsedByReports,
}: Props) => {
  const isCustomMeasure = isMeasureMeta(measure) && measure.custom;
  const isLocked = isCustomMeasure && measure.isLocked;
  const canDuplicate = isCustomMeasure || (isMeasureMeta(measure) && measure.capabilities?.isScalar === false);

  const handleExplain = useCallback(() => {
    onExplain(measure);
  }, [measure, onExplain]);

  const handleChartOfAccounts = useCallback(() => {
    onChartOfAccounts(measure);
  }, [measure, onChartOfAccounts]);

  const handleDuplicate = useCallback(() => {
    onDuplicate(measure);
  }, [measure, onDuplicate]);

  const handleEdit = useCallback(() => {
    onEdit(measure);
  }, [measure, onEdit]);

  const handleMoveToSubgroup = useCallback(() => {
    onMoveToSubgroup?.(measure);
  }, [measure, onMoveToSubgroup]);

  const handleDelete = useCallback(() => {
    onDelete(measure);
  }, [measure, onDelete]);

  const handleUsedByReports = useCallback(() => {
    onUsedByReports(measure);
  }, [onUsedByReports, measure]);

  const handleToggleLock = useCallback(() => {
    onToggleLock(measure);
  }, [onToggleLock, measure]);

  const toggleLockIcon = isLoading ? (
    <CircularProgress size={20} color={isLocked ? "primary" : "secondary"} />
  ) : isLocked ? (
    <LockOpenOutlinedIcon color="primary" />
  ) : (
    <LockOutlinedIcon color="secondary" />
  );

  return (
    <Menu
      open
      anchorReference="anchorPosition"
      anchorPosition={{ top: y + 15, left: x }}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 200 } } }}
    >
      <MenuItemStyled selected={false} onClick={handleExplain} disabled={isLoading}>
        <ListItemIcon sx={{ color: "secondary.main" }}>
          <InfoOutlinedIcon />
        </ListItemIcon>
        <Typography>Explain...</Typography>
      </MenuItemStyled>
      {measure.capabilities?.chartOfAccountsAllowed && (
        <MenuItemStyled onClick={handleChartOfAccounts} disabled={isLoading}>
          <ListItemIcon sx={{ color: "secondary.main" }}>
            <AccountTreeOutlinedIcon />
          </ListItemIcon>
          <Typography>Chart of Accounts...</Typography>
        </MenuItemStyled>
      )}

      {canDuplicate && !readonly && (
        <AuthorizedBox key="duplicate" permissions={["ManageCustomMeasures"]}>
          <MenuItemStyled onClick={handleDuplicate} disabled={isLoading}>
            <ListItemIcon sx={{ color: "secondary.main" }}>
              <ContentCopyOutlinedIcon />
            </ListItemIcon>
            <Typography>Duplicate</Typography>
          </MenuItemStyled>
        </AuthorizedBox>
      )}

      {isCustomMeasure && !isLocked && !readonly && (
        <AuthorizedBox key="edit" permissions={["ManageCustomMeasures"]}>
          <MenuItemStyled onClick={handleEdit} disabled={isLoading}>
            <ListItemIcon sx={{ color: "secondary.main" }}>
              <EditOutlinedIcon />
            </ListItemIcon>
            <Typography>Edit</Typography>
          </MenuItemStyled>
        </AuthorizedBox>
      )}

      {onMoveToSubgroup && isCustomMeasure && !isLocked && !readonly && (
        <AuthorizedBox key="moveToSubgroup" permissions={["ManageCustomMeasures"]}>
          <MenuItemStyled onClick={handleMoveToSubgroup} disabled={isLoading}>
            <ListItemIcon sx={{ color: "secondary.main" }}>
              <DriveFileMoveOutlinedIcon />
            </ListItemIcon>
            <Typography>Move to Subgroup</Typography>
          </MenuItemStyled>
        </AuthorizedBox>
      )}

      {isCustomMeasure && (
        <AuthorizedBox key="usedBy" permissions={["ManageReports"]}>
          <MenuItemStyled onClick={handleUsedByReports} disabled={isLoading}>
            <ListItemIcon sx={{ color: "secondary.main" }}>
              <FormatListBulletedRoundedIcon />
            </ListItemIcon>
            <Typography>Where-Used List</Typography>
          </MenuItemStyled>
        </AuthorizedBox>
      )}

      {isCustomMeasure && !readonly && (
        <AuthorizedBox key="toggleLock" permissions={["ManageCustomMeasures"]}>
          <MenuItemStyled onClick={handleToggleLock} disabled={isLoading}>
            <ListItemIcon>{toggleLockIcon}</ListItemIcon>
            <Typography>{isLocked ? "Unlock Editing" : "Lock Editing"}</Typography>
          </MenuItemStyled>
        </AuthorizedBox>
      )}

      {isCustomMeasure && !isLocked && !readonly && (
        <AuthorizedBox key="delete" permissions={["ManageCustomMeasures"]}>
          <MenuItemStyled onClick={handleDelete} disabled={isLoading}>
            <ListItemIcon sx={{ color: "error.main" }}>
              <DeleteOutlineIcon />
            </ListItemIcon>
            <Typography>Delete</Typography>
          </MenuItemStyled>
        </AuthorizedBox>
      )}
    </Menu>
  );
};

export default MeasureMenu;
